// Lector de códigos QR con la cámara del dispositivo.
//
// Dos caminos, en este orden:
//   1. `BarcodeDetector`, nativo del navegador. Es el más rápido y el que menos
//      batería gasta, pero hoy sólo existe en Chrome/Edge y en Android.
//   2. jsQR sobre un <canvas>. Funciona en todas partes —incluido Safari de
//      iOS, que es la mitad del parque de teléfonos— a cambio de algo más de
//      CPU. Sin este segundo camino, "escanea con el celular" sería falso para
//      medio mundo.
//
// El componente NO sabe qué hay dentro del código: devuelve el texto leído y
// quien lo usa decide. Así sirve igual para una placa de activo que para un
// lote o una ubicación.
import { useCallback, useEffect, useRef, useState } from 'react';
import jsQR from 'jsqr';
import { Camera, CameraOff, Loader2, X } from 'lucide-react';

// El navegador puede no traerlo: se declara suelto para no depender de los tipos.
interface DetectorCodigos { detect(fuente: CanvasImageSource): Promise<{ rawValue: string }[]> }
declare global {
  interface Window {
    BarcodeDetector?: { new (opciones?: { formats?: string[] }): DetectorCodigos;
                        getSupportedFormats?: () => Promise<string[]> };
  }
}

interface Props {
  /** Se llama con el texto de cada código nuevo. */
  onLeer: (texto: string) => void;
  onCerrar: () => void;
  titulo?: string;
  /** Milisegundos que se ignora un código ya leído, para no repetirlo mientras
   *  sigue delante de la cámara. */
  antirrebote?: number;
  /** Dentro de la página (bloque de alto fijo) en vez de pantalla completa encima
   *  de todo — regla del proyecto: sin ventanas modales. */
  integrado?: boolean;
}

export function EscanerQR({ onLeer, onCerrar, titulo = 'Escanear código', antirrebote = 2500, integrado = false }: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number | null>(null);
  const vistosRef = useRef<Map<string, number>>(new Map());
  const onLeerRef = useRef(onLeer); onLeerRef.current = onLeer;

  const [estado, setEstado] = useState<'iniciando' | 'leyendo' | 'error'>('iniciando');
  const [error, setError] = useState('');
  const [ultimo, setUltimo] = useState('');
  const [n, setN] = useState(0);

  // Un código que sigue delante de la cámara no puede contarse cincuenta veces.
  const emitir = useCallback((texto: string) => {
    const t = texto.trim();
    if (!t) return;
    const ahora = Date.now();
    const visto = vistosRef.current.get(t);
    if (visto && ahora - visto < antirrebote) return;
    vistosRef.current.set(t, ahora);
    setUltimo(t); setN((x) => x + 1);
    // Señal de que sí leyó: en una bodega ruidosa el sonido no siempre se oye.
    if (typeof navigator !== 'undefined' && navigator.vibrate) navigator.vibrate(60);
    onLeerRef.current(t);
  }, [antirrebote]);

  useEffect(() => {
    let cancelado = false;
    let detector: DetectorCodigos | null = null;

    const arrancar = async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error('Este navegador no permite usar la cámara. Escribe la placa a mano.');
        }
        // `environment` = cámara trasera, que es con la que se apunta a una placa.
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
        if (cancelado) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        const v = videoRef.current;
        if (v) { v.srcObject = stream; await v.play().catch(() => {}); }

        if (window.BarcodeDetector) {
          try { detector = new window.BarcodeDetector({ formats: ['qr_code'] }); }
          catch { detector = null; }
        }
        setEstado('leyendo');
        leer();
      } catch (e) {
        if (cancelado) return;
        const msg = e instanceof Error ? e.message : String(e);
        setError(msg.includes('Permission') || msg.includes('denied')
          ? 'No diste permiso de cámara. Habilítalo en el navegador y vuelve a intentar.'
          : msg);
        setEstado('error');
      }
    };

    const leer = () => {
      rafRef.current = requestAnimationFrame(leer);
      const v = videoRef.current, c = canvasRef.current;
      if (!v || !c || v.readyState !== v.HAVE_ENOUGH_DATA) return;

      if (detector) {
        void detector.detect(v)
          .then((cods) => { if (cods[0]?.rawValue) emitir(cods[0].rawValue); })
          .catch(() => { detector = null; });   // si falla, se sigue por jsQR
        return;
      }

      const ctx = c.getContext('2d', { willReadFrequently: true });
      if (!ctx) return;
      // Se reduce el cuadro antes de analizar: a tamaño completo, jsQR come CPU
      // y el teléfono se calienta en una toma física de doscientos activos.
      const ancho = 480;
      const alto = Math.round((v.videoHeight / v.videoWidth) * ancho) || 360;
      c.width = ancho; c.height = alto;
      ctx.drawImage(v, 0, 0, ancho, alto);
      const img = ctx.getImageData(0, 0, ancho, alto);
      const cod = jsQR(img.data, img.width, img.height, { inversionAttempts: 'dontInvert' });
      if (cod?.data) emitir(cod.data);
    };

    void arrancar();
    return () => {
      cancelado = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, [emitir]);

  return (
    <div className={integrado ? 'relative flex h-[75vh] flex-col overflow-hidden rounded-2xl bg-black' : 'fixed inset-0 z-50 flex flex-col bg-black/90'}>
      <div className="flex items-center justify-between px-4 py-3 text-white">
        <span className="flex items-center gap-2 text-sm font-medium"><Camera className="h-4 w-4" /> {titulo}</span>
        <button onClick={onCerrar} className="rounded p-1 hover:bg-white/10" aria-label="Cerrar"><X className="h-5 w-5" /></button>
      </div>

      <div className="relative flex flex-1 items-center justify-center overflow-hidden">
        <video ref={videoRef} playsInline muted className="h-full w-full object-cover" />
        <canvas ref={canvasRef} className="hidden" />

        {/* La mirilla: sin una referencia visual la gente no sabe dónde apuntar. */}
        {estado === 'leyendo' && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="h-56 w-56 rounded-lg border-4 border-white/80 shadow-[0_0_0_9999px_rgba(0,0,0,0.45)]" />
          </div>
        )}

        {estado === 'iniciando' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white">
            <Loader2 className="h-7 w-7 animate-spin" />
            <p className="text-sm">Abriendo la cámara…</p>
          </div>
        )}

        {estado === 'error' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-8 text-center text-white">
            <CameraOff className="h-8 w-8" />
            <p className="text-sm">{error}</p>
            <button onClick={onCerrar} className="rounded-md border border-white/40 px-3 py-1.5 text-sm hover:bg-white/10">
              Volver y escribir la placa
            </button>
          </div>
        )}
      </div>

      <div className="px-4 py-3 text-center text-white">
        {ultimo
          ? <p className="text-sm">Último leído: <b className="font-mono">{ultimo}</b> · {n} en esta sesión</p>
          : <p className="text-sm text-white/70">Apunta al código QR de la placa</p>}
      </div>
    </div>
  );
}
